<template>
  <div class="main">
    <b-card>
      <b-card-body>
        <b-row class="justify-content-between">
          <b-col cols="12">
            <b-col cols="12" class="mb-2 text-right">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="clearFilters"
              >
                Clear Filters
              </b-button>
            </b-col>
            <b-table
              v-if="!mainLoading"
              striped
              hover
              responsive
              :per-page="perPage"
              :current-page="currentPage"
              :items="filteredTimesheets()"
              :fields="timesheet_fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              @filtered="onFiltered"
            >

            <template #head(employeeName)="scope">
                <div class="d-flex flex-column">
                  <span>Employee</span>
                  <b-form-input
                    v-model="filters.employeeName"
                    placeholder="Filter by Name"
                    size="sm"
                    class="mt-1"
                  />
                </div>
              </template>

              <!-- Module Filter -->
              <template #head(moduleType)="scope">
                <div class="d-flex flex-column">
                  <span>Module</span>
                  <b-form-select
                    v-model="filters.module"
                    :options="[
                      { value: null, text: 'All' },
                      ...timesheetModules,
                    ]"
                    size="sm"
                    class="mt-1"
                  />
                </div>
              </template>

              <!-- Total Hours Filter -->
              <template #head(hoursWorked)="scope">
                <div class="d-flex flex-column">
                  <span>Total Hours</span>
                  <b-form-input
                    v-model="filters.hoursWorked"
                    placeholder="Filter by Hours"
                    size="sm"
                    class="mt-1"
                  />
                </div>
              </template>

              <!-- Status Filter -->
              <template #head(status)="scope">
                <div class="d-flex flex-column">
                  <span>Status</span>
                  <b-form-select
                    v-model="filters.status"
                    size="sm"
                    class="mt-1"
                  >
                    <option :value="null">All</option>
                    <option
                      v-for="status in timesheetStatus"
                      :key="status.value"
                      :value="status.value"
                    >
                      {{ status.text }}
                    </option>
                  </b-form-select>
                </div>
              </template>

              <!-- Comment Filter -->
              <template #head(taskDescription)="scope">
                <div class="d-flex flex-column">
                  <span>Comment</span>
                  <b-form-input
                    v-model="filters.taskDescription"
                    placeholder="Filter by Comment"
                    size="sm"
                    class="mt-1"
                  />
                </div>
              </template>

              <!-- Submitted On Filter -->
              <template #head(date)="scope">
                <div class="d-flex flex-column date-filter-container">
                  <span>Submitted On</span>
                  <b-form-datepicker
                    v-model="filters.date"
                    placeholder="Select Date"
                    size="sm"
                    class="mt-1"
                    @input="currentPage = 1"
                    :date-format-options="{
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }"
                  />
                </div>
              </template>
              
              

              <template #cell(date)="data">
                {{ formatDateDDMMMYYYY(data.item.date) }}
              </template>

              <template v-slot:cell(actions)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  class="m-1"
                  :disabled="data.item.status != 'PENDING'"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>

                  <b-dropdown-item
                    v-if="checkPermissions(data.item)"
                    @click="timesheetAction('APPROVED', data.item)"
                  >
                    <feather-icon icon="CheckIcon" />
                    <span class="align-middle ml-50">Approve</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="checkPermissions(data.item)"
                    @click="timesheetAction('REJECTED', data.item)"
                  >
                    <feather-icon icon="XIcon" />
                    <span class="align-middle ml-50">Reject</span>
                  </b-dropdown-item>

                  <!-- <b-dropdown-item @click="viewTimesheet(data.item)">
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-50">View</span>
                  </b-dropdown-item> -->
                </b-dropdown>
              </template>
            </b-table>
          </b-col>
          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="mt-2"
            />
            <b-form-group>
              Showing
              {{ totalRows > 0 ? perPage * (currentPage - 1) + 1 : 0 }}
              to
              {{
                totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0
              }}
              of
              {{ totalRows > 0 ? totalRows : 0 }}
              entries
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BSpinner,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCard,
  BDropdown,
  BLink,
  BDropdownItem,
  BTab,
  BTabs,
  BFormDatepicker,
  BFormTextarea,
  BFormTimepicker,
  BForm,
  BCardBody,
} from "bootstrap-vue";

import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import { customError, fnSortingDropdown } from "@/libs/customFn";
import { TimesheetStatus, TimesheetModules } from "@/libs/defaultList";
import { IsAdmin } from "@/@core/utils/validations/permissionCheck";
import timesheet from "@/store/timesheet";
export default {
  components: {
    BCard,
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    BDropdown,
    BLink,
    BDropdownItem,
    BTab,
    BTabs,
    BFormDatepicker,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    BFormTimepicker,
    BForm,
    BCardBody,
  },

  data() {
    return {
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: ["employeeID", "taskDescription"],
      filterOn: [],
      timesheets: [],
      timesheet_fields: [
        {
          key: "employeeName",
          label: "Employee",
          sortable: true,
          filter: true,
        },
        { key: "moduleType", label: "Module", sortable: true },
        { key: "hoursWorked", label: "Total Hours", sortable: true },
        { key: "status", label: "Status", sortable: true },
        {
          key: "taskDescription",
          label: "Comment",
          sortable: true,
          filter: true,
        },
        { key: "date", label: "Submitted On", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      moduleReference: "HRMS",
      timesheetStatus: TimesheetStatus,
      timesheetModules: TimesheetModules,
      userId: null,
      mainLoading: false,
      projectOptions: [],
      employeeOptions: [],
      employeeListOptions: [],
      submitting: false,
      // validation rules
      required,
      email,
      userList: [],
      filters: {
        reference: "",
        status: null,
        module: null,
        employeeName: "",
        hoursWorked: "",
        taskDescription: "",
        date: "",
      },
    };
  },

  computed: {
    ...mapGetters("employeeModule", {
      employeeList: "employees",
      loading: "loading",
    }),
    ...mapGetters("serviceModule", {
      timesheetList: "timesheetList",
      timesheet: "timesheet",
      loading: "loading",
    }),
    ...mapGetters("userModule", {
      users: "users",
      loading: "loading",
    }),

    formattedTimesheets() {
      return this.timesheets.map((timesheet) => {
        return {
          ...timesheet,
          employeeName: this.getEmployee(timesheet.employeeID), // Map employeeID to full name
        };
      });
    },
  },
  methods: {
    ...mapActions("serviceModule", [
      "getServiceTimesheetListAction",
      "getServiceTimesheetAction",
      "postServiceTimesheetAction",
      "putServiceTimesheetAction",
    ]),
    ...mapActions("employeeModule", ["getEmployeeListAction"]),
    ...mapActions("userModule", ["getUserListByRoleAction"]),

    formatDate2(date) {
      if (!date) return "-";
      return new Date(date).toISOString().split("T")[0]; // Returns YYYY-MM-DD
    },
    
    formatDateDDMMMYYYY(dateStr) {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
},
    getEmployee(id) {
      const employee = this.employeeList.find(
        (s) => s.id === id || s.refId === id
      );
      return employee ? employee.firstName + " " + employee.lastName : "-";
    },

    checkPermissions(item) {
      console.log("Item Status:", item.status);
      console.log(
        "Module Type:",
        item.moduleType,
        "Expected:",
        this.moduleReference
      );
      console.log("User is Admin:", IsAdmin());
      console.log("User ID:", this.userId, "Approver ID:", item.approverID);

      return (
        item.status === "PENDING" &&
        (IsAdmin() || this.userId === item.approverID)
      );
    },

    getEmployeeUser(id){
    //console.log("iddddddddddddddddddddddddddddddddddd", id);
    const employee = this.employeeList.find((s) => s.refId === id);
    //console.log("employeeeeeeeeeeeeeeeeeeeeeee", employee);
    return  employee == null ? '' : employee.id;
  },
  clearFilters() {
  this.filters = {
    reference: "",
    status: null,
    module: null,
    employeeName: "",
    hoursWorked: "",
    taskDescription: "",
    date: "",
  };
  this.currentPage = 1;
},
  success(message) {
      this.$swal({
        title: "Timesheet Status Updated!",
        text: message,
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    async timesheetAction(status, timesheet) {
      try {
        await this.getServiceTimesheetAction({
          modulereference: timesheet.moduleType,
          id: timesheet.id,
        });

        // confirm update
        const result = await this.$swal({
          title: "Are you sure?",
          text: "You will not be able to revert your changes!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, update status!",
          cancelButtonText: "Cancel",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });

        if (result.isConfirmed) {
          const payload = {
            id: timesheet.id,
            approverID: timesheet.approverID,
            status: status,
            note: status,
            moduleReference: timesheet.moduleType,
          };

          try {
            await this.putServiceTimesheetAction(payload).then(() => {
              this.success();
              //this.fetchTimesheetData();
              if(!IsAdmin())
              {
                this.getServiceTimesheetListAction().then(() => {
                this.timesheets = this.timesheetList.filter(
              (x) => x.approverID == userid
            );
                this.totalRows = this.timesheets.length;
              
            });
              }
              else{
                this.getServiceTimesheetListAction().then(() => {
                this.timesheets = this.timesheetList;
                this.totalRows = this.timesheets.length;
              
            });
              }
            });
          } catch (updateError) {
            console.error("Error updating timesheet:", updateError);
            await this.$swal({
              title: "Error",
              text: "An error occurred while updating the timesheet.",
              icon: "error",
              customClass: {
                confirmButton: "btn btn-danger",
              },
            });
          }
        }
      } catch (error) {
        console.error("Error fetching timesheet:", error);
        await this.$swal({
          title: "Error",
          text: "An error occurred while fetching the timesheet data.",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-danger",
          },
        });
      }
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
      this.totalRows = filteredItems.length;
    },

    getStatusVariant(status) {
      switch (status) {
        case "PENDING":
          return "light-info";
        case "APPROVE":
          return "light-warning";
        case "REJECT":
          return "light-danger";
        case "DONE":
          return "light-success";
        default:
          return "secondary";
      }
    },

    async fetchTimesheetData(id, userid) {
      this.mainLoading = true;
      try {
        let loader = this.$swal({
          title: "Loading...",
          text: "Please wait while the details are loading...",
          allowEscapeKey: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });

        if (IsAdmin()) {
          await this.getServiceTimesheetListAction().then(() => {
            this.timesheets = this.timesheetList;
            this.totalRows = this.timesheets.length;
          });
        } else {
        
          await this.getServiceTimesheetListAction().then(() => {
            this.timesheets = this.timesheetList.filter(
              (x) => x.approverID == userid
            );
            console.log("this.timesheets", this.timesheets);
            this.totalRows = this.timesheets.length;
          });
        }

        await this.getUserListByRoleAction("staff");
        let allUser = this.users.filter((z) => z.id !== this.userId);
        allUser = allUser.map((user) => {
          return { text: `${user.firstName} ${user.lastName}`, value: user.id };
        });
        this.userList = fnSortingDropdown(allUser);

        loader.close();
      } catch (error) {
        console.log(error);
      } finally {
        this.mainLoading = false;
      }
    },

    filteredTimesheets() {
      return this.formattedTimesheets.filter((ts) => {
        return (
          // Employee Name Filter
          (!this.filters.employeeName ||
            ts.employeeName
              .toLowerCase()
              .includes(this.filters.employeeName.toLowerCase())) &&
          // Module Filter
          (!this.filters.module || ts.moduleType === this.filters.module) &&
          // Total Hours Filter
          (!this.filters.hoursWorked ||
            ts.hoursWorked.toString().includes(this.filters.hoursWorked)) &&
          // Status Filter
          // (!this.filters.status || ts.status === this.filters.status) &&
          (!this.filters.status ||
            this.filters.status === null ||
            ts.status === this.filters.status) && // Allow 'All'
          // Task Description (Comment) Filter
          (!this.filters.taskDescription ||
            (ts.taskDescription &&
              ts.taskDescription
                .toLowerCase()
                .includes(this.filters.taskDescription.toLowerCase()))) &&
          // Submitted On (Date) Filter
          (!this.filters.date ||
            this.formatDateDDMMMYYYY(ts.date) === this.formatDateDDMMMYYYY(this.filters.date))
        );
      });
    },
  },

  async mounted() {
    await this.getEmployeeListAction().then(() => {
      if (!IsAdmin()) {

        let employee = this.employeeList.filter(
          (x) => x.refId == this.userId
        )[0];
        if (employee) {
          this.formData.employeeID = employee.id;
          this.editFormData.employeeID = employee.id;
          //this.timesheetId.employeeID = employee.id;
        } else {
          customError("You do not have any employee profile!");
        }
      }

      this.employeeOptions = this.employeeList
        .filter((x) => IsAdmin() || x.refId != this.userId)
        .map((employee) => ({
          value: employee.id,
          text: `${employee.firstName} ${employee.lastName}`,
        }));

      this.employeeListOptions = this.employeeList.map((employee) => ({
        value: employee.id,
        text: `${employee.firstName} ${employee.lastName}`,
      }));
    });

    const userData = JSON.parse(localStorage.getItem("userData"));
    console.log("🚀 ~ mounted ~ userData:", IsAdmin);
    this.userId = userData.id;

    const emp = this.getEmployeeUser(this.userId);
    console.log("userData", userData);
    console.log("emppp", emp);
    await this.fetchTimesheetData(emp, this.userId);

  },

  setup(props) {
    return { IsAdmin };
  },
};
</script>
<style scoped>
/* Ensure the parent column allows overflow */
.date-filter-container {
  position: relative;
  z-index: 1051; /* Above table/card z-index */
  overflow: visible !important;
}

/* Make sure the datepicker popup isn’t cut off */
.b-form-datepicker,
.b-calendar,
.b-calendar .dropdown-menu {
  z-index: 2050 !important;
}

/* If table cuts it off, allow overflow */
.table-responsive {
  overflow: visible !important;
}

</style>
